import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { oxpayAPI } from "@/lib/oxpay";
import { Button } from "@/components/ui/button";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import useConfirmDialog from "@/hooks/useConfirmDialog";
import { Badge } from "@/components/ui/badge";
import Pagination from "@/components/Pagination";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "react-toastify";
import { subDays } from "date-fns";
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Eye,
  Edit,
  Search,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Wallet,
  Loader2,
} from "lucide-react";

/** Same OxPay wallet body shape as MyCard (`walletInfo.funds.available.amount`). */
function formatFundSlice(fund) {
  if (!fund || fund.amount == null || fund.amount === "") return "-";
  const cur = fund.currency || "USD";
  const n = parseFloat(fund.amount);
  const amt = Number.isNaN(n)
    ? String(fund.amount)
    : n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  return `${cur} ${amt}`;
}

/** OxPay date_range: YYYYMMDD,YYYYMMDD (inclusive 90-day window: end − 89 days … end). */
function formatDateYYYYMMDD(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
}

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState("lastSignInAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showBalanceDialog, setShowBalanceDialog] = useState(false);
  const [selectedUserForBalance, setSelectedUserForBalance] = useState(null);
  const [walletInfo, setWalletInfo] = useState(null);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [loadingOlderTransactions, setLoadingOlderTransactions] = useState(false);
  const [transactionPeriodEnd, setTransactionPeriodEnd] = useState(new Date());
  const [transactionPeriodStart, setTransactionPeriodStart] = useState(() =>
    subDays(new Date(), 89),
  );
  const [transactionHasOlderPeriod, setTransactionHasOlderPeriod] =
    useState(false);
  const confirmDialog = useConfirmDialog();
  const TRANSACTION_CUTOFF_DATE = new Date("2025-07-01");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase.rpc("get_users_with_accounts");

      if (error) throw error;

      setUsers(data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    setDetailsLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_user_details", {
        p_user_id: userId,
      });

      if (error) throw error;

      setUserDetails(data);
      setEditData({
        firstName: data.account?.first_name || "",
        lastName: data.account?.last_name || "",
        dateOfBirth: data.account?.birthday || "",
        nationality: data.account?.nationality || "",
        phoneNumber: data.account?.phone_number || "",
        kycStatus: data.account?.kyc_status || "not_started",
        oxpayStatus: data.account?.oxpay_status || "not_created",
        poaStatus: data.account?.poa_status || "not_submitted",
      });
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error("Failed to load user details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleToggleClick = (userId, currentStatus, oxpayUserId) => {
    if (!oxpayUserId) {
      toast.error("OxPay user not created");
      return;
    }

    confirmDialog.openDialog({
      userId,
      oxpayUserId,
      currentStatus,
    });
  };

  const confirmToggleStatus = async () => {
    console.log("confirmToggleStatus called");
    const { userId, oxpayUserId, currentStatus } =
      confirmDialog.dialogState.data;
    const newStatus = currentStatus === "active" ? "suspended" : "active";
    console.log("Toggle from", currentStatus, "to", newStatus);

    await confirmDialog.executeAction(async () => {
      console.log("Executing action - calling OxPay API");

      let response;

      // Call appropriate OxPay API based on current status
      if (currentStatus === "active") {
        // Deactivate user - call deleteUser
        console.log("Calling deleteUser to deactivate");
        response = await oxpayAPI.deleteUser(oxpayUserId);
      } else {
        // Reactivate user - call createUser
        console.log("Calling createUser to reactivate");
        response = await oxpayAPI.createUser({
          id: oxpayUserId,
        });
      }

      console.log("OxPay API Response:", response);

      // Check if the API call was successful
      // OxPay API typically returns success in headers.status_code
      if (response?.headers?.status_code === "success") {
        // Update local database
        const { error } = await supabase.rpc("update_user_account", {
          p_user_id: userId,
          p_oxpay_status: newStatus,
        });

        if (error) throw error;

        toast.success(
          `User ${
            newStatus === "active" ? "activated" : "deactivated"
          } successfully`,
        );
        fetchUsers();
      } else {
        console.error("Failed response:", response);
        throw new Error(
          response?.body?.description || "Failed to update OxPay status",
        );
      }
    });
  };

  const handleUpdateUser = async () => {
    try {
      const { error } = await supabase.rpc("update_user_account", {
        p_user_id: selectedUser.id,
        p_first_name: editData.firstName || null,
        p_last_name: editData.lastName || null,
        p_date_of_birth: editData.dateOfBirth || null,
        p_nationality: editData.nationality || null,
        p_phone_number: editData.phoneNumber || null,
        p_kyc_status: editData.kycStatus || null,
        p_oxpay_status: editData.oxpayStatus || null,
        p_poa_status: editData.poaStatus || null,
      });

      if (error) throw error;

      toast.success("User account updated successfully");
      setEditMode(false);
      fetchUsers();
      fetchUserDetails(selectedUser.id);
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Failed to update user");
    }
  };

  const handleViewBalance = async (
    user,
    periodOverride = null,
    append = false,
    fetchBalance = true,
  ) => {
    setSelectedUserForBalance(user);
    setShowBalanceDialog(true);
    setBalanceLoading(!append);
    setLoadingOlderTransactions(append);
    if (!append) {
      setTransactionHistory([]);
    }

    try {
      const oxpayUserId = user.account?.oxpayUserId;
      if (!oxpayUserId) {
        toast.error("OxPay user ID not found");
        setBalanceLoading(false);
        return;
      }

      // Balance is needed only when opening dialog, not when loading older periods.
      if (fetchBalance) {
        setWalletInfo(null);
        const balanceResponse = await oxpayAPI.getWallet(oxpayUserId);
        if (balanceResponse?.headers?.status_code === "error") {
          throw new Error(
            balanceResponse?.body?.description || "Failed to fetch balance",
          );
        }
        setWalletInfo(balanceResponse?.body ?? null);
      }

      const basePeriodEnd = !append && !periodOverride ? new Date() : transactionPeriodEnd;
      const periodEnd = periodOverride?.end || basePeriodEnd;
      const periodStart =
        periodOverride?.start || (!append && !periodOverride ? subDays(periodEnd, 89) : transactionPeriodStart);

      if (!append || periodOverride) {
        setTransactionPeriodEnd(periodEnd);
        setTransactionPeriodStart(periodStart);
      }

      const dateRange = `${formatDateYYYYMMDD(periodStart)},${formatDateYYYYMMDD(periodEnd)}`;

      // Fetch one slice (90-day window), append when loading older periods
      const transactionsResponse = await oxpayAPI.getTransactions(oxpayUserId, {
        page: 1,
        limit: 50,
        date_range: dateRange,
      });
      if (
        transactionsResponse?.headers?.status_code === "success" ||
        transactionsResponse?.headers?.status_code !== "error"
      ) {
        const transactions =
          transactionsResponse?.body?.transactions ||
          transactionsResponse?.body?.data ||
          transactionsResponse?.body ||
          [];
        const transactionsArray = Array.isArray(transactions)
          ? transactions
          : [];
        if (append) {
          setTransactionHistory((prev) => [...prev, ...transactionsArray]);
        } else {
          setTransactionHistory(transactionsArray);
        }
        setTransactionHasOlderPeriod(periodStart > TRANSACTION_CUTOFF_DATE);
      } else {
        console.warn(
          "Failed to fetch transactions:",
          transactionsResponse?.body?.description,
        );
        if (!append) {
          setTransactionHistory([]);
        }
        setTransactionHasOlderPeriod(periodStart > TRANSACTION_CUTOFF_DATE);
      }
    } catch (error) {
      console.error("Error fetching balance and transactions:", error);
      toast.error("Failed to load balance and transaction history");
      if (!append) {
        setTransactionHistory([]);
      }
    } finally {
      setBalanceLoading(false);
      setLoadingOlderTransactions(false);
    }
  };

  const handleLoadOlderTransactions = async () => {
    if (!selectedUserForBalance || !transactionHasOlderPeriod) return;

    const newPeriodEnd = subDays(transactionPeriodStart, 1);
    const newPeriodStart = subDays(newPeriodEnd, 89);

    await handleViewBalance(
      selectedUserForBalance,
      { start: newPeriodStart, end: newPeriodEnd },
      true,
      false,
    );
  };

  const getOxPayStatusStyle = (status) => {
    const styles = {
      not_created: {
        bgColor: "bg-gray-100 dark:bg-gray-800",
        textColor: "text-gray-700 dark:text-gray-300",
        icon: XCircle,
        text: "Not Created",
      },
      active: {
        bgColor: "bg-green-100 dark:bg-green-900",
        textColor: "text-green-700 dark:text-green-300",
        icon: CheckCircle,
        text: "Active",
      },
      suspended: {
        bgColor: "bg-red-100 dark:bg-red-900",
        textColor: "text-red-700 dark:text-red-300",
        icon: AlertCircle,
        text: "Suspended",
      },
      pending_kyc: {
        bgColor: "bg-yellow-100 dark:bg-yellow-900",
        textColor: "text-yellow-700 dark:text-yellow-300",
        icon: Clock,
        text: "Pending KYC",
      },
      kyc_in_progress: {
        bgColor: "bg-blue-100 dark:bg-blue-900",
        textColor: "text-blue-700 dark:text-blue-300",
        icon: Clock,
        text: "KYC In Progress",
      },
      kyc_approved: {
        bgColor: "bg-green-100 dark:bg-green-900",
        textColor: "text-green-700 dark:text-green-300",
        icon: CheckCircle,
        text: "KYC Approved",
      },
      kyc_rejected: {
        bgColor: "bg-red-100 dark:bg-red-900",
        textColor: "text-red-700 dark:text-red-300",
        icon: XCircle,
        text: "KYC Rejected",
      },
      closed: {
        bgColor: "bg-gray-100 dark:bg-gray-800",
        textColor: "text-gray-700 dark:text-gray-300",
        icon: XCircle,
        text: "Closed",
      },
    };
    return styles[status] || styles.not_created;
  };

  const getKycStatusStyle = (status) => {
    const styles = {
      not_started: {
        bgColor: "bg-gray-100 dark:bg-gray-800",
        textColor: "text-gray-700 dark:text-gray-300",
        icon: XCircle,
        text: "Not Started",
      },
      pending: {
        bgColor: "bg-yellow-100 dark:bg-yellow-900",
        textColor: "text-yellow-700 dark:text-yellow-300",
        icon: Clock,
        text: "Pending",
      },
      processing: {
        bgColor: "bg-blue-100 dark:bg-blue-900",
        textColor: "text-blue-700 dark:text-blue-300",
        icon: Clock,
        text: "Processing",
      },
      approved: {
        bgColor: "bg-green-100 dark:bg-green-900",
        textColor: "text-green-700 dark:text-green-300",
        icon: CheckCircle,
        text: "Approved",
      },
      rejected: {
        bgColor: "bg-red-100 dark:bg-red-900",
        textColor: "text-red-700 dark:text-red-300",
        icon: XCircle,
        text: "Rejected",
      },
      expired: {
        bgColor: "bg-orange-100 dark:bg-orange-900",
        textColor: "text-orange-700 dark:text-orange-300",
        icon: AlertCircle,
        text: "Expired",
      },
    };
    return styles[status] || styles.not_started;
  };

  const getPoaStatusStyle = (status) => {
    const styles = {
      not_submitted: {
        bgColor: "bg-gray-100 dark:bg-gray-800",
        textColor: "text-gray-700 dark:text-gray-300",
        icon: XCircle,
        text: "Not Submitted",
      },
      pending: {
        bgColor: "bg-yellow-100 dark:bg-yellow-900",
        textColor: "text-yellow-700 dark:text-yellow-300",
        icon: Clock,
        text: "Pending",
      },
      verified: {
        bgColor: "bg-green-100 dark:bg-green-900",
        textColor: "text-green-700 dark:text-green-300",
        icon: CheckCircle,
        text: "Verified",
      },
      rejected: {
        bgColor: "bg-red-100 dark:bg-red-900",
        textColor: "text-red-700 dark:text-red-300",
        icon: XCircle,
        text: "Rejected",
      },
    };
    return styles[status] || styles.not_submitted;
  };

  // Legacy function - kept for potential future use
  // const getStatusBadge = (status, type = "oxpay") => { ... }

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Filter users based on search query
  const filteredUsers = users.filter((user) => {
    const searchLower = searchQuery.toLowerCase();
    const fullName = user.account
      ? `${user.account.firstName || ""} ${user.account.lastName || ""}`
          .trim()
          .toLowerCase()
      : "";

    return (
      user.email.toLowerCase().includes(searchLower) ||
      fullName.includes(searchLower) ||
      (user.account?.phoneNumber &&
        user.account.phoneNumber.includes(searchQuery))
    );
  });

  // Sort function
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aValue, bValue;

    switch (sortColumn) {
      case "email":
        aValue = a.email || "";
        bValue = b.email || "";
        break;
      case "name":
        aValue = a.account
          ? `${a.account.firstName || ""} ${a.account.lastName || ""}`.trim()
          : "";
        bValue = b.account
          ? `${b.account.firstName || ""} ${b.account.lastName || ""}`.trim()
          : "";
        break;
      case "lastSignInAt":
        aValue = a.lastSignInAt ? new Date(a.lastSignInAt).getTime() : 0;
        bValue = b.lastSignInAt ? new Date(b.lastSignInAt).getTime() : 0;
        break;
      default:
        aValue = a.lastSignInAt ? new Date(a.lastSignInAt).getTime() : 0;
        bValue = b.lastSignInAt ? new Date(b.lastSignInAt).getTime() : 0;
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
  });

  // Calculate pagination
  const totalPages = Math.ceil(sortedUsers.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  // Sort icon component
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4 ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" />
    );
  };

  // Reset page when search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortColumn, sortDirection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Card className="border-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Users Management</h2>
            <p className="text-muted-foreground">
              Manage FellasCard users and their accounts
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline">Total: {users.length}</Badge>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email, name, or phone number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          {searchQuery && (
            <Badge variant="secondary" className="self-start">
              {filteredUsers.length} result
              {filteredUsers.length !== 1 ? "s" : ""}
            </Badge>
          )}
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button
                    onClick={() => handleSort("email")}
                    className="flex items-center hover:text-foreground transition-colors cursor-pointer"
                  >
                    Email
                    <SortIcon column="email" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort("name")}
                    className="flex items-center hover:text-foreground transition-colors cursor-pointer"
                  >
                    Name
                    <SortIcon column="name" />
                  </button>
                </TableHead>
                <TableHead>OxPay Status</TableHead>
                <TableHead>KYC Status</TableHead>
                <TableHead>PoA Status</TableHead>
                <TableHead>Cards</TableHead>
                <TableHead>
                  <button
                    onClick={() => handleSort("lastSignInAt")}
                    className="flex items-center hover:text-foreground transition-colors cursor-pointer"
                  >
                    Last Sign In
                    <SortIcon column="lastSignInAt" />
                  </button>
                </TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell>
                    {user.account
                      ? `${user.account.firstName || ""} ${
                          user.account.lastName || ""
                        }`.trim() || "-"
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const status = user.account?.oxpayStatus || "not_created";
                      const style = getOxPayStatusStyle(status);
                      const Icon = style.icon;
                      const canToggle =
                        status === "active" || status === "suspended";

                      return (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!canToggle || !user.account?.oxpayUserId}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 h-auto rounded-full text-xs font-medium ${style.bgColor} ${style.textColor} hover:opacity-80 disabled:opacity-100 disabled:cursor-default`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (canToggle && user.account?.oxpayUserId) {
                              handleToggleClick(
                                user.id,
                                status,
                                user.account.oxpayUserId,
                              );
                            }
                          }}
                        >
                          <Icon className="h-3 w-3" />
                          {style.text}
                        </Button>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const status = user.account?.kycStatus || "not_started";
                      const style = getKycStatusStyle(status);
                      const Icon = style.icon;

                      return (
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${style.bgColor} ${style.textColor}`}
                        >
                          <Icon className="h-3 w-3" />
                          {style.text}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const status = user.account?.poaStatus || "not_submitted";
                      const style = getPoaStatusStyle(status);
                      const Icon = style.icon;

                      return (
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${style.bgColor} ${style.textColor}`}
                        >
                          <Icon className="h-3 w-3" />
                          {style.text}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {user.activeCardsCount || 0}/{user.cardsCount || 0}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(user.lastSignInAt)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(user);
                              fetchUserDetails(user.id);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>User Details</DialogTitle>
                            <DialogDescription>{user.email}</DialogDescription>
                          </DialogHeader>

                          {detailsLoading ? (
                            <div className="flex items-center justify-center h-32">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            </div>
                          ) : userDetails ? (
                            <div className="space-y-6">
                              <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                  <h3 className="text-lg font-semibold">
                                    Account Information
                                  </h3>
                                  {!editMode && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setEditMode(true)}
                                    >
                                      <Edit className="h-4 w-4 mr-2" />
                                      Edit
                                    </Button>
                                  )}
                                </div>

                                {editMode ? (
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label>First Name</Label>
                                      <Input
                                        value={editData.firstName}
                                        onChange={(e) =>
                                          setEditData({
                                            ...editData,
                                            firstName: e.target.value,
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Last Name</Label>
                                      <Input
                                        value={editData.lastName}
                                        onChange={(e) =>
                                          setEditData({
                                            ...editData,
                                            lastName: e.target.value,
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Date of Birth</Label>
                                      <Input
                                        type="date"
                                        value={editData.dateOfBirth}
                                        onChange={(e) =>
                                          setEditData({
                                            ...editData,
                                            dateOfBirth: e.target.value,
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Nationality</Label>
                                      <Input
                                        value={editData.nationality}
                                        onChange={(e) =>
                                          setEditData({
                                            ...editData,
                                            nationality: e.target.value,
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Phone Number</Label>
                                      <Input
                                        value={editData.phoneNumber}
                                        onChange={(e) =>
                                          setEditData({
                                            ...editData,
                                            phoneNumber: e.target.value,
                                          })
                                        }
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>KYC Status</Label>
                                      <Select
                                        value={editData.kycStatus}
                                        onValueChange={(value) =>
                                          setEditData({
                                            ...editData,
                                            kycStatus: value,
                                          })
                                        }
                                      >
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="not_started">
                                            Not Started
                                          </SelectItem>
                                          <SelectItem value="pending">
                                            Pending
                                          </SelectItem>
                                          <SelectItem value="processing">
                                            Processing
                                          </SelectItem>
                                          <SelectItem value="approved">
                                            Approved
                                          </SelectItem>
                                          <SelectItem value="rejected">
                                            Rejected
                                          </SelectItem>
                                          <SelectItem value="expired">
                                            Expired
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>OxPay Status</Label>
                                      <Select
                                        value={editData.oxpayStatus}
                                        onValueChange={(value) =>
                                          setEditData({
                                            ...editData,
                                            oxpayStatus: value,
                                          })
                                        }
                                      >
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="not_created">
                                            Not Created
                                          </SelectItem>
                                          <SelectItem value="pending_kyc">
                                            Pending KYC
                                          </SelectItem>
                                          <SelectItem value="kyc_in_progress">
                                            KYC In Progress
                                          </SelectItem>
                                          <SelectItem value="kyc_approved">
                                            KYC Approved
                                          </SelectItem>
                                          <SelectItem value="kyc_rejected">
                                            KYC Rejected
                                          </SelectItem>
                                          <SelectItem value="active">
                                            Active
                                          </SelectItem>
                                          <SelectItem value="suspended">
                                            Suspended
                                          </SelectItem>
                                          <SelectItem value="closed">
                                            Closed
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>PoA Status</Label>
                                      <Select
                                        value={editData.poaStatus}
                                        onValueChange={(value) =>
                                          setEditData({
                                            ...editData,
                                            poaStatus: value,
                                          })
                                        }
                                      >
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="not_submitted">
                                            Not Submitted
                                          </SelectItem>
                                          <SelectItem value="pending">
                                            Pending
                                          </SelectItem>
                                          <SelectItem value="verified">
                                            Verified
                                          </SelectItem>
                                          <SelectItem value="rejected">
                                            Rejected
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="col-span-2 flex gap-2">
                                      <Button
                                        variant="outline"
                                        onClick={() => setEditMode(false)}
                                        className="flex-1"
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        onClick={handleUpdateUser}
                                        className="flex-1"
                                      >
                                        Save Changes
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <p className="text-sm text-muted-foreground">
                                        Name
                                      </p>
                                      <p className="font-medium">
                                        {userDetails.account
                                          ? `${
                                              userDetails.account.first_name ||
                                              ""
                                            } ${
                                              userDetails.account.last_name ||
                                              ""
                                            }`.trim() || "-"
                                          : "-"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">
                                        Date of Birth
                                      </p>
                                      <p className="font-medium">
                                        {userDetails.account?.birthday || "-"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">
                                        Nationality
                                      </p>
                                      <p className="font-medium">
                                        {userDetails.account?.nationality ||
                                          "-"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">
                                        Phone
                                      </p>
                                      <p className="font-medium">
                                        {userDetails.account?.phone_number ||
                                          "-"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">
                                        OxPay User ID
                                      </p>
                                      <p className="font-medium">
                                        {userDetails.account?.oxpay_user_id ||
                                          "-"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">
                                        user account ID
                                      </p>
                                      <p className="font-medium">
                                        {userDetails.account
                                          ?.oxpay_account_id || "-"}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-sm text-muted-foreground">
                                        Daily Limit
                                      </p>
                                      <p className="font-medium">
                                        $
                                        {userDetails.account?.daily_limit ||
                                          "5,000"}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {userDetails.cards &&
                                userDetails.cards.length > 0 && (
                                  <div className="space-y-4">
                                    <h3 className="text-lg font-semibold">
                                      Cards ({userDetails.cards.length})
                                    </h3>
                                    <div className="space-y-2">
                                      {userDetails.cards.map((card) => (
                                        <div
                                          key={card.id}
                                          className="border rounded-lg p-4 space-y-2"
                                        >
                                          <div className="flex justify-between items-start">
                                            <div>
                                              <p className="font-medium">
                                                {card.cardBrand} {card.cardType}
                                              </p>
                                              <p className="text-sm text-muted-foreground">
                                                Order: {card.orderId}
                                              </p>
                                              {card.username && (
                                                <p className="text-sm text-muted-foreground">
                                                  Username: {card.username}
                                                </p>
                                              )}
                                            </div>
                                            <div className="flex gap-2">
                                              <Badge
                                                variant={
                                                  card.cardStatus === "active"
                                                    ? "success"
                                                    : card.cardStatus ===
                                                        "shipped"
                                                      ? "default"
                                                      : card.cardStatus ===
                                                          "delivered"
                                                        ? "success"
                                                        : "secondary"
                                                }
                                              >
                                                {card.cardStatus}
                                              </Badge>
                                              <Badge
                                                variant={
                                                  card.paymentStatus === "paid"
                                                    ? "success"
                                                    : card.paymentStatus ===
                                                        "pending"
                                                      ? "default"
                                                      : "destructive"
                                                }
                                              >
                                                {card.paymentStatus}
                                              </Badge>
                                            </div>
                                          </div>
                                          {card.trackingNumber && (
                                            <p className="text-sm">
                                              Tracking: {card.trackingNumber}
                                            </p>
                                          )}
                                          <p className="text-xs text-muted-foreground">
                                            Created:{" "}
                                            {formatDate(card.createdAt)}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                            </div>
                          ) : null}
                        </DialogContent>
                      </Dialog>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewBalance(user)}
                        disabled={!user.account?.oxpayUserId}
                        title="View Balance & Transaction History"
                      >
                        <Wallet className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {sortedUsers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={sortedUsers.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
          />
        )}
      </CardContent>

      {/* Balance & Transaction History Dialog */}
      <Dialog open={showBalanceDialog} onOpenChange={setShowBalanceDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Balance & Transaction History</DialogTitle>
            <DialogDescription>
              {selectedUserForBalance?.email}
            </DialogDescription>
          </DialogHeader>

          {balanceLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Balance: same OxPay body as MyCard (walletInfo.funds.*) */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Wallet</h3>
                {walletInfo ? (
                  <div className="max-w-md rounded-2xl border bg-muted/30 p-5 space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Available</p>
                      <p className="text-3xl font-bold tracking-tight">
                        {formatFundSlice(walletInfo?.funds?.available)}
                      </p>
                    </div>
                    {walletInfo?.funds?.pending != null &&
                      walletInfo.funds.pending.amount != null &&
                      String(walletInfo.funds.pending.amount) !== "" && (
                        <div>
                          <p className="text-sm text-muted-foreground">
                            Pending
                          </p>
                          <p className="text-xl font-semibold text-amber-600 dark:text-amber-500">
                            {formatFundSlice(walletInfo.funds.pending)}
                          </p>
                        </div>
                      )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    No balance data available
                  </p>
                )}
              </div>

              {/* Transaction History Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  Transaction History ({transactionHistory.length})
                </h3>
                {transactionHistory.length > 0 ? (
                  <>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Merchant</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Balance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {transactionHistory.map((tx) => (
                            <TableRow key={tx.id || tx.transaction_id}>
                              <TableCell className="text-xs">
                                {tx.date
                                  ? new Date(tx.date).toLocaleString()
                                  : "-"}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {tx.type || "-"}
                                </Badge>
                              </TableCell>
                              <TableCell
                                className={`font-medium ${
                                  tx.type === "TOPUP"
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {tx.type === "TOPUP" ? "+" : "-"}$
                                {Math.abs(
                                  parseFloat(tx.amount || 0),
                                ).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </TableCell>
                              <TableCell>{tx.merchant || "-"}</TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    tx.status?.toUpperCase() === "COMPLETED" ||
                                    tx.status?.toUpperCase() === "SUCCESS"
                                      ? "secondary"
                                      : tx.status?.toUpperCase() === "PENDING"
                                        ? "default"
                                        : "destructive"
                                  }
                                  className={
                                    tx.status?.toUpperCase() === "COMPLETED" ||
                                    tx.status?.toUpperCase() === "SUCCESS"
                                      ? "bg-green-500 text-white hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 border-transparent"
                                      : ""
                                  }
                                >
                                  {tx.status || "-"}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs">
                                $
                                {parseFloat(tx.balance || 0).toLocaleString(
                                  "en-US",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {transactionHasOlderPeriod && (
                      <div className="mt-3 flex justify-center">
                        <Button
                          variant="outline"
                          onClick={handleLoadOlderTransactions}
                          disabled={loadingOlderTransactions}
                        >
                          {loadingOlderTransactions ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            "More"
                          )}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-3">
                    <p className="text-muted-foreground">
                      No transaction history available
                    </p>
                    {transactionHasOlderPeriod && (
                      <div className="flex justify-center">
                        <Button
                          variant="outline"
                          onClick={handleLoadOlderTransactions}
                          disabled={loadingOlderTransactions}
                        >
                          {loadingOlderTransactions ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            "More"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Toggle Confirmation Dialog */}
      <ConfirmActionDialog
        open={confirmDialog.dialogState.open}
        onOpenChange={(open) => {
          // Only close dialog if not loading and user wants to close
          if (!open && !confirmDialog.dialogState.loading) {
            confirmDialog.closeDialog();
          }
        }}
        title={
          confirmDialog.dialogState.data?.currentStatus === "active"
            ? "Deactivate User?"
            : "Activate User?"
        }
        description={
          confirmDialog.dialogState.data?.currentStatus === "active"
            ? "This will suspend the user's user account. They will not be able to use their cards until reactivated."
            : "This will activate the user's user account. They will be able to use their cards again."
        }
        confirmText={
          confirmDialog.dialogState.data?.currentStatus === "active"
            ? "Deactivate"
            : "Activate"
        }
        cancelText="Cancel"
        onConfirm={confirmToggleStatus}
        loading={confirmDialog.dialogState.loading}
        loadingText="Processing status change..."
        success={confirmDialog.dialogState.success}
        successText={
          confirmDialog.dialogState.data?.currentStatus === "active"
            ? "User deactivated successfully"
            : "User activated successfully"
        }
        error={confirmDialog.dialogState.error}
        variant={
          confirmDialog.dialogState.data?.currentStatus === "active"
            ? "destructive"
            : "default"
        }
      />
    </Card>
  );
};

export default Users;
